import type { AcademicYearEnum, Gender } from './athletes';
import type { BudgetStatusEnum } from './budgets';
import type { BudgetCategoryType } from './budgets.categorized';
import type { ApiAthleteTag } from './tags';
import { convertCentsToDollarsDecimal, convertDecimalToPercentage, convertDollarsToCentsDecimal, convertPercentageToDecimal, turnTwrnIntoId } from '@/utils/helpers';
import { formatDateWithoutTime, formatDateWithTime } from '@/utils/dates';
import type { ApiPaginationParams, ApiSortParams } from './api';
import { cloneDeep, isNil } from 'lodash-es';
import { TeamPosition } from './positions';
import { i18n } from '@/plugins/i18n';
import type { AthleteDisplayItemProps } from '@/components/common/AthleteDisplayItem.vue';
import { isAthleteRecruit } from './athletes';
import type { ScholarshipPackageListing } from './scholarships';

export interface ApiTeamBudgetPositionTarget {
  total_budget: number;
  position_id: string;
}

export interface ApiAwardCategory {
  twrn: string;
  amount: number;
}

export interface ApiTeamBudgetAthleteScholarship {
  equivalency_percentage: number;
  package_twrn: string | null;
  award_categories: ApiAwardCategory[];
}

export interface ApiBudgetCategory {
  amount: number;
  exclude_from_totals: boolean;
  name: string;
  type: BudgetCategoryType;
}

export interface ApiBudgetCategories {
  [categoryTwrn: string]: ApiBudgetCategory;
}

export interface ApiTeamBudgetAthletePosition {
  uuid: string;
  order: number | null;
}

export interface ApiTeamAthleteBudgetLineItem {
  allocation: number;
  scholarship_percentage: number;
  scholarship_allocation: number;
  nil_earnings: number;
  has_roster_spot: boolean;
  created_at: string | null;
  updated_at: string | null;
  institutional_nil_categories: ApiBudgetCategories;
  third_party_categories: ApiBudgetCategories;
}

export interface ApiInstitutionalNil {
  budgeted_amount: number;
  committed_amount: number;
  contracted_amount: number;
  paid_amount: number;
}

export class InstitutionalNilOverview {
  budgeted: number;
  committed: number;
  contracted: number;
  paid: number;

  constructor(data: ApiInstitutionalNil) {
    this.budgeted = convertCentsToDollarsDecimal(data.budgeted_amount);
    this.committed = convertCentsToDollarsDecimal(data.committed_amount);
    this.contracted = convertCentsToDollarsDecimal(data.contracted_amount);
    this.paid = convertCentsToDollarsDecimal(data.paid_amount);
  }
}


export interface ApiScholarship {
  budgeted: number;
  committed: number;
}

export class ScholarshipsAmount {
  budgeted: number;
  committed: number;

  constructor(data: ApiScholarship) {
    this.budgeted = convertCentsToDollarsDecimal(data.budgeted);
    this.committed = convertCentsToDollarsDecimal(data.committed);
  }
}

export class ScholarshipsIssued {
  budgeted: number;
  committed: number;

  constructor(data: ApiScholarship) {
    this.budgeted = convertPercentageToDecimal(data.budgeted);
    this.committed = convertPercentageToDecimal(data.committed);
  }
}

export interface ApiTeamBudgetAthlete {
  twrn: string;
  recruit_id: number | null;
  axle_twrn: string | null;
  first_name: string;
  last_name: string;
  email: string;
  gender: Gender;
  profile_photo: string | null;
  jersey_number: string | null;
  academic_year_start: string;
  academic_year_end: string;
  position: ApiTeamBudgetAthletePosition | null;
  line_item: ApiTeamAthleteBudgetLineItem;
  was_manually_created: boolean;
  is_active: boolean;
  tags: string[];
  scholarship: ApiTeamBudgetAthleteScholarship | null;
  removed_from_roster_at: string | null;
}

export class TeamBudgetAthlete {
  twrn: string;
  axleTwrn: string | null;
  recruitId: number | null;

  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  gender: Gender;
  profilePhoto: string | null;
  jerseyNumber: string | null;

  isActive: boolean;
  position: string;
  order: number | null;
  rosterSpot: boolean;
  allocation: number;
  scholarshipAllocation: number;
  scholarshipPercentage: number;
  lineItem: ApiTeamAthleteBudgetLineItem | null;
  scholarship: ApiTeamBudgetAthleteScholarship | null;
  awardCategories: {
    twrn: string;
    amount: number;
  }[];

  removedFromRosterAt: string | null;
  scholarshipCalculatedValue: number;
  equivalencyPercentage: number;
  packageTwrn: string | null;
  wasManuallyCreated: boolean;
  tags: string[];

  constructor(data: ApiTeamBudgetAthlete, teamTwrn?: string) {
    this.twrn = data.twrn;
    this.axleTwrn = data.axle_twrn;
    this.recruitId = data.recruit_id;

    this.firstName = data.first_name;
    this.lastName = data.last_name;
    this.fullName = `${data.first_name} ${data.last_name}`;
    this.email = data.email;
    this.gender = data.gender;
    this.profilePhoto = data.profile_photo;
    this.jerseyNumber = data.jersey_number;

    this.isActive = data.is_active;
    this.order = data.position?.order ?? null;
    const resolvedFromUuid = teamTwrn ? TeamPosition.resolveName(teamTwrn, data.position?.uuid ?? null) : null;
    this.position = resolvedFromUuid ?? i18n.global.t('unspecified');
    this.rosterSpot = data.line_item?.has_roster_spot ?? true;
    this.allocation = convertCentsToDollarsDecimal(data.line_item?.allocation ?? 0);
    this.scholarship = data.scholarship;
    this.scholarshipAllocation = convertCentsToDollarsDecimal(data.line_item?.scholarship_allocation ?? 0);
    this.scholarshipPercentage = convertPercentageToDecimal(data.line_item?.scholarship_percentage ?? 0);
    this.awardCategories = data.scholarship?.award_categories ?? [];
    this.removedFromRosterAt = data.removed_from_roster_at;
    this.packageTwrn = data.scholarship?.package_twrn ?? null;
    this.wasManuallyCreated = data.was_manually_created;
    this.tags = data.tags;
    this.lineItem = data.line_item;
    const rawEquivalency = data.scholarship?.equivalency_percentage ?? 0;
    this.equivalencyPercentage = Math.min(Math.max(Number(rawEquivalency) || 0, 0), 100);
    this.scholarshipCalculatedValue = data.scholarship?.award_categories?.reduce((acc, category) => acc + category.amount, 0) ?? 0;
  }

  static fromApi(data: ApiTeamBudgetAthlete, teamTwrn?: string) {
    return new TeamBudgetAthlete(data, teamTwrn);
  }

  /**
   * Returns props for AthleteDisplayItem component
   */
  toDisplayProps(options?: {
    academicYear?: AcademicYearEnum | null;
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    layout?: 'row' | 'column';
    onClick?: () => void;
    hideChips?: boolean;
  }): AthleteDisplayItemProps {
    return {
      name: this.fullName,
      avatar: {
        src: this.profilePhoto,
        useInitials: true,
      },
      jerseyNumber: this.jerseyNumber,
      position: this.position,
      academicYear: options?.academicYear ?? null,
      showDeactivatedChip: this.removedFromRosterAt !== null || !this.isActive,
      wasManuallyCreated: this.wasManuallyCreated,
      isRecruit: isAthleteRecruit(this.axleTwrn, this.recruitId),
      size: options?.size ?? 'medium',
      layout: options?.layout ?? 'row',
      onClick: options?.onClick,
      hideChips: options?.hideChips,
    };
  }
}

export interface ApiTeamBudgetExpanded {
  twrn: string;
  name: string;
  team_twrn: string;
  academic_year_date_id: string;
  institutional_nil: ApiInstitutionalNil;
  scholarships_issued: ScholarshipsIssued;
  scholarships_amount: ScholarshipsAmount;
  third_party_target: number;
  third_party_nil: number;
  roster_size: number;
  status: BudgetStatusEnum;
  created_at: string;
  updated_at: string;
  athletes: ApiTeamBudgetAthlete[];
  position_targets: ApiTeamBudgetPositionTarget[];
  tag_definitions: ApiAthleteTag[];
  period_start_at: string;
  period_end_at: string;
  institutional_nil_categories: ApiBudgetCategories;
  third_party_categories: ApiBudgetCategories;
  institutional_categories_total: number;
  third_party_categories_total: number;
}

export type ApiTeamBudget = Pick<ApiTeamBudgetExpanded,
  | 'twrn'
  | 'name'
  | 'status'
  | 'academic_year_date_id'
  | 'institutional_nil'
  | 'scholarships_issued'
  | 'scholarships_amount'
  | 'third_party_target'
  | 'third_party_nil'
  | 'roster_size'
  | 'updated_at'
>;

export type ApiTeamBudgetCompact = Pick<ApiTeamBudgetExpanded,
  // NOT FINAL
  | 'twrn'
  | 'name'
>;

export interface ApiTeamBudgetGroupExpanded {
  twrn: string;
  team_twrn: string;
  name: string;
  status: BudgetStatusEnum;
  period_start_at: string;
  period_end_at: string;
  updated_at: string;
  budgets: ApiTeamBudgetExpanded[];
}

export type ApiTeamBudgetGroup = Pick<ApiTeamBudgetGroupExpanded,
  | 'twrn'
  | 'team_twrn'
  | 'name'
  | 'status'
  | 'period_start_at'
  | 'period_end_at'
  | 'updated_at'
> & {
  budgets: ApiTeamBudget[];
};

export type ApiTeamBudgetGroupCompact = Pick<ApiTeamBudgetGroupExpanded,
  | 'twrn'
  | 'team_twrn'
  | 'name'
  | 'status'
  | 'period_start_at'
  | 'period_end_at'
  | 'updated_at'
>;

export interface ApiBudgetYearData {
  institutional_nil: number;
  scholarships_value: number;
  scholarships_count: number;
  estimated_third_party_nil: number;
  roster_size: number;
}

export interface ApiCreateTeamBudgetGroupRequest {
  name: string;
  start_academic_year_date_id: string;
  budget_years: ApiBudgetYearData[];
}

export type ApiDuplicateTeamBudgetGroupRequest = ApiCreateTeamBudgetGroupRequest;

export type ApiEditTeamBudgetGroupRequest = ApiCreateTeamBudgetGroupRequest;

export interface ApiGetTeamBudgetGroupsParams extends ApiPaginationParams {
  keyword?: string;
  status?: BudgetStatusEnum[];
  academic_year_date_ids?: string[];
  teams?: string[];
  sort?: ApiSortParams;
}

export interface ApiGetTeamBudgetGroupsCompactParams {
  keyword?: string;
  status?: BudgetStatusEnum[];
  academic_year_date_ids?: string[];
  teams?: string[];
}

export interface ApiTeamBudgetGroupListCompactBudget {
  twrn: string;
  name: string;
  academic_year_date_id: string;
  status: BudgetStatusEnum;
  academic_year_name?: string;
}

export interface ApiTeamBudgetGroupListCompact {
  twrn: string;
  team_twrn: string;
  name: string;
  status: BudgetStatusEnum;
  budgets: ApiTeamBudgetGroupListCompactBudget[];
}

abstract class TeamBudgetBase {
  readonly twrn: string;
  readonly name: string;

  protected constructor(data: ApiTeamBudgetCompact) {
    this.twrn = data.twrn;
    this.name = data.name;
  }
}

export class TeamBudgetCompact extends TeamBudgetBase {
  static fromApi(data: ApiTeamBudgetCompact) {
    return new TeamBudgetCompact(data);
  }

  constructor(data: ApiTeamBudgetCompact) {
    super(data);
  }
}

export class TeamBudget extends TeamBudgetBase {
  status: BudgetStatusEnum;
  academicYearDateId: string;
  institutionalNil: InstitutionalNilOverview;
  scholarshipsIssued: ScholarshipsIssued;
  scholarshipsAmount: ScholarshipsAmount;
  thirdPartyTarget: number;
  thirdPartyNil: number;
  rosterSize: number;
  updatedAt: string;

  static fromApi(data: ApiTeamBudget) {
    return new TeamBudget(data);
  }

  constructor(data: ApiTeamBudget) {
    super(data);
    this.status = data.status;
    this.academicYearDateId = data.academic_year_date_id;
    this.institutionalNil = new InstitutionalNilOverview(data.institutional_nil);
    this.scholarshipsIssued = new ScholarshipsIssued(data.scholarships_issued);
    this.scholarshipsAmount = new ScholarshipsAmount(data.scholarships_amount);
    this.thirdPartyTarget = convertCentsToDollarsDecimal(data.third_party_target);
    this.thirdPartyNil = convertCentsToDollarsDecimal(data.third_party_nil);
    this.rosterSize = data.roster_size;
    this.updatedAt = formatDateWithTime(new Date(data.updated_at), 'MMM dd, yyyy, h:mm aa');
  }
}

// Client-side classes (similar to existing budget classes)
export class BudgetCategory {
  amount: number;
  excludeFromTotals: boolean;
  name: string;
  type: BudgetCategoryType;
  twrn: string;

  constructor(twrn: string, data: ApiBudgetCategory) {
    this.twrn = twrn;
    this.amount = convertCentsToDollarsDecimal(data.amount);
    this.excludeFromTotals = data.exclude_from_totals;
    this.name = data.name;
    this.type = data.type;
  }
}

// Client side class for displaying the team budget tables
export class BudgetGroupAthleteItem {
  id: string;
  twrn: string;
  axleTwrn: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  recruitId: number | null;
  email: string;
  wasManuallyCreated: boolean;
  position: string;
  gender: Gender;
  profilePhoto: string | null;
  jerseyNumber: string | null;
  isActive: boolean;
  removedFromRosterAt: string | null;
  isRecruit: boolean;
  displayProps: AthleteDisplayItemProps;
  tags: string[];
  budgets: Record<string, {
    budgetTwrn: string;
    order: number | null;
    equivalencyPercentage: number;
    rosterSpot: boolean;
    scholarshipPercentage: number;
    totalScholarshipAllocation: number;
    lineItem: ApiTeamAthleteBudgetLineItem | null;
    totalInstitutionalNilAllocation: number;
    totalThirdPartyNilAllocation: number;
    scholarship: ApiTeamBudgetAthleteScholarship | null;
    scholarshipCalculatedValue: number;
    originalScholarshipValue?: number;
  }>;

  constructor(data: TeamBudgetAthlete) {
    this.id = `${data.twrn}-${data.position}`;
    this.twrn = data.twrn;
    this.axleTwrn = data.axleTwrn;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.fullName = data.fullName;
    this.recruitId = data.recruitId;
    this.email = data.email;
    this.wasManuallyCreated = data.wasManuallyCreated;
    this.position = data.position;
    this.gender = data.gender;
    this.profilePhoto = data.profilePhoto;
    this.jerseyNumber = data.jerseyNumber;
    this.isActive = data.isActive;
    this.removedFromRosterAt = data.removedFromRosterAt;
    this.isRecruit = isAthleteRecruit(data.axleTwrn, data.recruitId);
    this.displayProps = data.toDisplayProps();
    this.tags = data.tags;
    this.budgets = {};
  }

  addBudgetData(athlete: TeamBudgetAthlete, budgetTwrn: string) {
    const nilCategories = athlete.lineItem?.institutional_nil_categories ?? {};
    const institutionalNilTotal = Object.values(nilCategories).reduce((acc, category) => acc + category.amount, 0);

    const thirdPartyNilCategories = athlete.lineItem?.third_party_categories ?? {};
    const thirdPartyNilTotal = Object.values(thirdPartyNilCategories).reduce((acc, category) => acc + category.amount, 0);

    this.budgets[budgetTwrn] = {
      budgetTwrn,
      order: athlete.order,
      equivalencyPercentage: athlete.equivalencyPercentage,
      rosterSpot: athlete.rosterSpot,
      scholarshipPercentage: athlete.scholarshipPercentage,
      totalScholarshipAllocation: athlete.scholarshipAllocation,
      lineItem: athlete.lineItem,
      totalInstitutionalNilAllocation: convertCentsToDollarsDecimal(institutionalNilTotal),
      totalThirdPartyNilAllocation: convertCentsToDollarsDecimal(thirdPartyNilTotal),
      scholarship: null,
      scholarshipCalculatedValue: athlete.scholarshipCalculatedValue,
      originalScholarshipValue: athlete.scholarshipCalculatedValue ?? 0,
    };

    if (athlete.scholarship) {
      this.budgets[budgetTwrn].scholarship = {
        ...athlete.scholarship,
        equivalency_percentage: convertPercentageToDecimal(athlete.scholarship?.equivalency_percentage ?? 0),
      };
    }
  };

  clone(): BudgetGroupAthleteItem {
    return cloneDeep(this);
  }

  withUpdatedScholarship(budgetTwrn: string, scholarship: ApiTeamBudgetAthleteScholarship | null): BudgetGroupAthleteItem {
    const clone = this.clone();
    const budget = clone.budgets[budgetTwrn];
    if (!budget) {
      throw new Error(`[BudgetGroupAthleteItem] Budget with TWRN ${budgetTwrn} not found`);
    }

    if (scholarship) {
      budget.scholarship = {
        equivalency_percentage: scholarship.equivalency_percentage,
        package_twrn: scholarship.package_twrn,
        award_categories: scholarship.award_categories.map((category) => ({
          twrn: category.twrn,
          amount: category.amount,
        })),
      };
      budget.scholarshipCalculatedValue = scholarship.award_categories.reduce((sum, category) => sum + category.amount, 0);
    } else {
      budget.scholarship = null;
      budget.scholarshipCalculatedValue = 0;
    }

    return clone;
  }

  getCategoryOverage(
    budgetTwrn: string,
    categoryTwrn: string,
    scholarshipPackages: Record<string, Record<string, ScholarshipPackageListing>>,
  ): {
    currentValue: number;
    originalValue: number;
  } | null {
    const budget = this.budgets[budgetTwrn];
    if (!budget?.scholarship?.package_twrn) return null;
    const pkg = scholarshipPackages[budgetTwrn]?.[budget.scholarship.package_twrn];
    if (!pkg) return null;
    const currentCat = budget.scholarship.award_categories.find((c) => c.twrn === categoryTwrn);
    const originalCat = pkg.award_categories.find((c) => c.twrn === categoryTwrn);
    // A category the package does not define has an implicit $0 limit, so any
    // amount on the athlete exceeds it. This happens when an award category is
    // added after the package was saved.
    const currentAmount = currentCat?.amount ?? 0;
    const originalAmount = originalCat?.amount ?? 0;
    if (currentAmount <= originalAmount) return null;
    return {
      currentValue: convertCentsToDollarsDecimal(currentAmount),
      originalValue: convertCentsToDollarsDecimal(originalAmount),
    };
  }

  hasAnyOverage(
    scholarshipPackages: Record<string, Record<string, ScholarshipPackageListing>>,
  ): boolean {
    for (const [, budget] of Object.entries(this.budgets)) {
      if (!budget.scholarship?.package_twrn) continue;
      const pkg = scholarshipPackages[budget.budgetTwrn]?.[budget.scholarship.package_twrn];
      if (!pkg) continue;
      for (const cat of budget.scholarship.award_categories) {
        const originalCat = pkg.award_categories.find((c) => c.twrn === cat.twrn);
        // Categories the package does not define have an implicit $0 limit.
        const originalAmount = originalCat?.amount ?? 0;
        if (cat.amount > originalAmount) return true;
      }
    }
    return false;
  }

  /**
   * Returns props for AthleteDisplayItem component
   */
  toDisplayProps(options?: {
    academicYear?: AcademicYearEnum | null;
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    layout?: 'row' | 'column';
    onClick?: () => void;
    hideChips?: boolean;
  }): AthleteDisplayItemProps {
    return {
      name: this.fullName,
      avatar: {
        src: this.profilePhoto,
        useInitials: true,
      },
      jerseyNumber: this.jerseyNumber,
      position: this.position,
      academicYear: options?.academicYear ?? null,
      showDeactivatedChip: this.removedFromRosterAt !== null || !this.isActive,
      wasManuallyCreated: this.wasManuallyCreated,
      isRecruit: isAthleteRecruit(this.axleTwrn, this.recruitId),
      size: options?.size ?? 'medium',
      layout: options?.layout ?? 'row',
      onClick: options?.onClick,
      hideChips: options?.hideChips,
    };
  }
}

export const BUDGET_GROUPED_BY = {
  POSITION: 'position',
  NONE: 'none',
} as const;

export type BudgetGroupedBy = (typeof BUDGET_GROUPED_BY)[keyof typeof BUDGET_GROUPED_BY];

/**
 * Column-id prefix for the multi-year "All Years" total column. Rendered only when a
 * budget group links 2+ academic years, and it always totals every linked year
 * regardless of the active budget-period filter.
 */
export const ALL_YEARS_COL_ID = 'allYears';

export class TeamBudgetExpanded extends TeamBudget {
  periodStartAt: string;
  periodEndAt: string;
  createdAt: string;
  athletes: TeamBudgetAthlete[];
  positionTargets: ApiTeamBudgetPositionTarget[];
  tagDefinitions: ApiAthleteTag[];
  institutionalNilCategories: ApiBudgetCategories;
  thirdPartyCategories: ApiBudgetCategories;
  institutionalNilCategoriesTotal: number;
  thirdPartyCategoriesTotal: number;
  teamTwrn: string;
  assignedRosterSize: number;

  static fromApi(data: ApiTeamBudgetExpanded) {
    return new TeamBudgetExpanded(data);
  }

  constructor(data: ApiTeamBudgetExpanded) {
    super(data);
    this.periodStartAt = formatDateWithoutTime(new Date(data.period_start_at));
    this.periodEndAt = formatDateWithoutTime(new Date(data.period_end_at));
    this.createdAt = formatDateWithTime(new Date(data.created_at), 'MMM dd, yyyy, h:mm aa');
    this.athletes = data.athletes.map((athlete) => TeamBudgetAthlete.fromApi(athlete, data.team_twrn));
    this.positionTargets = data.position_targets;
    this.tagDefinitions = data.tag_definitions;
    this.institutionalNilCategories = data.institutional_nil_categories;
    this.thirdPartyCategories = data.third_party_categories;
    this.institutionalNilCategoriesTotal = convertCentsToDollarsDecimal(data.institutional_categories_total);
    this.thirdPartyCategoriesTotal = convertCentsToDollarsDecimal(data.third_party_categories_total);
    this.teamTwrn = data.team_twrn;
    this.assignedRosterSize = data.athletes.filter((athlete) => TeamBudgetAthlete.fromApi(athlete, data.team_twrn).rosterSpot).length;
  }
}

abstract class BudgetGroupBase {
  readonly twrn: string;
  readonly name: string;
  readonly id: string;
  readonly teamTwrn: string;
  readonly status: BudgetStatusEnum;
  readonly periodStartAt: string;
  readonly periodEndAt: string;
  readonly updatedAt: string;

  protected constructor(data: ApiTeamBudgetGroupCompact) {
    this.twrn = data.twrn;
    this.name = data.name;
    this.id = turnTwrnIntoId(data.twrn);
    this.teamTwrn = data.team_twrn;
    this.status = data.status;
    this.periodStartAt = formatDateWithoutTime(new Date(data.period_start_at));
    this.periodEndAt = formatDateWithoutTime(new Date(data.period_end_at));
    this.updatedAt = formatDateWithTime(new Date(data.updated_at), 'MMM dd, yyyy, h:mm aa');
  }
}

export class BudgetGroupCompact extends BudgetGroupBase {
  static fromApi(data: ApiTeamBudgetGroupCompact) {
    return new BudgetGroupCompact(data);
  }

  constructor(data: ApiTeamBudgetGroupCompact) {
    super(data);
  }
}

export class BudgetGroup extends BudgetGroupBase {
  budgets: TeamBudget[];
  institutionalNilTotals: InstitutionalNilOverview;
  scholarshipsAmountTotals: ScholarshipsAmount;
  scholarshipsIssuedTotals: ScholarshipsIssued;
  thirdPartyNilTotals: number;
  updatedAt: string;

  static fromApi(data: ApiTeamBudgetGroup) {
    return new BudgetGroup(data);
  }

  constructor(data: ApiTeamBudgetGroup) {
    super(data);
    const lastUpdatedTimestamp = data.budgets.map((budget) => new Date(budget.updated_at).getTime()).sort((a, b) => b - a)[0];
    this.updatedAt = lastUpdatedTimestamp ? formatDateWithTime(new Date(lastUpdatedTimestamp), 'MMM dd, yyyy, h:mm aa') : '';

    this.budgets = data.budgets.map(TeamBudget.fromApi);

    // Calculate total values across all budgets

    this.institutionalNilTotals = this.budgets.reduce((acc, budget) => {
      return {
        budgeted: acc.budgeted + budget.institutionalNil.budgeted,
        committed: acc.committed + budget.institutionalNil.committed,
        contracted: acc.contracted + budget.institutionalNil.contracted,
        paid: acc.paid + budget.institutionalNil.paid,
      };
    }, {
      budgeted: 0,
      committed: 0,
      contracted: 0,
      paid: 0,
    });

    this.scholarshipsAmountTotals = this.budgets.reduce((acc, budget) => {
      return {
        budgeted: acc.budgeted + budget.scholarshipsAmount.budgeted,
        committed: acc.committed + budget.scholarshipsAmount.committed,
      };
    }, {
      budgeted: 0,
      committed: 0,
    });

    this.scholarshipsIssuedTotals = this.budgets.reduce((acc, budget) => {
      return {
        budgeted: acc.budgeted + budget.scholarshipsIssued.budgeted,
        committed: acc.committed + budget.scholarshipsIssued.committed,
      };
    }, {
      budgeted: 0,
      committed: 0,
    });

    this.thirdPartyNilTotals = this.budgets.reduce((acc, budget) => {
      return acc + budget.thirdPartyNil;
    }, 0);
  }

  toEditRequest(): ApiEditTeamBudgetGroupRequest {
    if (isNil(this.budgets[0])) {
      throw new Error('[BudgetGroup] No budgets in the budget group');
    }

    return {
      name: this.name,
      start_academic_year_date_id: this.budgets[0].academicYearDateId,
      budget_years: this.budgets.map((b) => ({
        estimated_third_party_nil: convertDollarsToCentsDecimal(b.thirdPartyTarget),
        institutional_nil: convertDollarsToCentsDecimal(b.institutionalNil.budgeted),
        roster_size: b.rosterSize,
        scholarships_count: convertDecimalToPercentage(b.scholarshipsIssued.budgeted),
        scholarships_value: convertDollarsToCentsDecimal(b.scholarshipsAmount.budgeted),
      })),
    };
  }
}

export class BudgetGroupExpanded extends BudgetGroup {
  budgets: TeamBudgetExpanded[];

  static fromApi(data: ApiTeamBudgetGroupExpanded) {
    return new BudgetGroupExpanded(data);
  }

  constructor(data: ApiTeamBudgetGroupExpanded) {
    super(data);
    this.budgets = data.budgets.map(TeamBudgetExpanded.fromApi);
  }
}

export class TeamBudgetGroupListCompactBudget {
  readonly twrn: string;
  readonly name: string;
  readonly academicYearDateId: string;
  readonly status: BudgetStatusEnum;
  readonly academicYearName?: string;

  constructor(data: ApiTeamBudgetGroupListCompactBudget) {
    this.twrn = data.twrn;
    this.name = data.name;
    this.academicYearDateId = data.academic_year_date_id;
    this.status = data.status;
    this.academicYearName = data.academic_year_name;
  }

  static fromApi(data: ApiTeamBudgetGroupListCompactBudget) {
    return new TeamBudgetGroupListCompactBudget(data);
  }
}

export class BudgetGroupListCompact {
  readonly twrn: string;
  readonly name: string;
  readonly teamTwrn: string;
  readonly status: BudgetStatusEnum;
  readonly budgets: TeamBudgetGroupListCompactBudget[];

  constructor(data: ApiTeamBudgetGroupListCompact) {
    this.twrn = data.twrn;
    this.name = data.name;
    this.teamTwrn = data.team_twrn;
    this.status = data.status;
    this.budgets = data.budgets.map(TeamBudgetGroupListCompactBudget.fromApi);
  }

  static fromApi(data: ApiTeamBudgetGroupListCompact) {
    return new BudgetGroupListCompact(data);
  }
}
